// <brook-marquee>: its pause button stops and restarts the scrolling row (WCAG 2.2.2). The
// visitor's choice sticks until they press it again. While keyboard focus is on one of its
// links, the row is a still, scrollable strip, so the focused link scrolls into view; it
// starts moving again from the beginning when focus leaves. Used by brook-marquee.
//
// Its images are lazy, but the row slides them into view with a CSS transform, not a scroll,
// and Safari (and sometimes Chrome) only rechecks lazy images on a scroll or touch, so tiles
// came in as empty boxes until the visitor touched the screen. Once the marquee itself (which
// doesn't move) is near the screen, all its images, both copies, load at once.
//
// Visitors can also drag the row sideways, with a finger or the mouse (#90). A drag pauses the
// CSS animation and sets its time from how far the pointer moved, wrapping around the loop, so
// the row follows the pointer in both directions; on release it moves on from there unless the
// pause button is on. A press that moves less than DRAG_THRESHOLD px is a tap and opens the
// link; after a drag the click is swallowed. Vertical swipes still scroll the page
// (touch-action: pan-y in the CSS); the browser then cancels the pointer and nothing happens.
if (!customElements.get('brook-marquee')) {
  const DRAG_THRESHOLD = 8;
  const loadImages = (marquee) =>
    marquee.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
  const observer =
    'IntersectionObserver' in window &&
    new IntersectionObserver(
      (entries) => {
        entries.forEach(({ isIntersecting, target }) => {
          if (!isIntersecting) return;
          observer.unobserve(target);
          loadImages(target);
        });
      },
      { rootMargin: '600px 0px' }
    );

  customElements.define(
    'brook-marquee',
    class extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('.brook-marquee__pause');
        this.button?.addEventListener('click', this.toggle);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        this.addEventListener('pointerdown', this.onPointerDown);
        this.addEventListener('pointermove', this.onPointerMove);
        this.addEventListener('pointerup', this.onPointerUp);
        this.addEventListener('pointercancel', this.onPointerUp);
        this.addEventListener('click', this.onClick, true);
        this.addEventListener('dragstart', this.onDragStart);
        if (observer) observer.observe(this);
        else loadImages(this);
      }

      disconnectedCallback() {
        if (observer) observer.unobserve(this);
        this.button?.removeEventListener('click', this.toggle);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('focusout', this.onFocusOut);
        this.removeEventListener('pointerdown', this.onPointerDown);
        this.removeEventListener('pointermove', this.onPointerMove);
        this.removeEventListener('pointerup', this.onPointerUp);
        this.removeEventListener('pointercancel', this.onPointerUp);
        this.removeEventListener('click', this.onClick, true);
        this.removeEventListener('dragstart', this.onDragStart);
      }

      // The row's running CSS animation; none while keyboard browsing (animation: none).
      get animation() {
        return this.querySelector('.brook-marquee__track')?.getAnimations()[0];
      }

      onPointerDown = (event) => {
        if (!event.isPrimary || event.button !== 0) return;
        if (event.target.closest('.brook-marquee__pause') || this.classList.contains('is-browsing')) return;
        const animation = this.animation;
        const list = this.querySelector('.brook-marquee__list');
        if (!animation || !list) return;
        this.wasDragged = false;
        this.press = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          animation,
          width: list.getBoundingClientRect().width,
          duration: animation.effect.getComputedTiming().duration,
          // A row moving right plays the animation backwards, so time runs the other way.
          direction: getComputedStyle(animation.effect.target).animationDirection === 'reverse' ? -1 : 1,
          dragging: false,
        };
      };

      onPointerMove = (event) => {
        const press = this.press;
        if (!press || event.pointerId !== press.id) return;
        // Released outside the strip before the drag began (no capture yet): end the press.
        if (!(event.buttons & 1)) return this.onPointerUp(event);
        const dx = event.clientX - press.x;
        if (!press.dragging) {
          if (Math.abs(dx) < DRAG_THRESHOLD || Math.abs(dx) < Math.abs(event.clientY - press.y)) return;
          press.dragging = true;
          press.time = press.animation.currentTime;
          press.animation.pause();
          this.classList.add('is-dragging');
          this.setPointerCapture?.(event.pointerId);
        }
        const time = press.time - press.direction * (dx / press.width) * press.duration;
        press.animation.currentTime = ((time % press.duration) + press.duration) % press.duration;
      };

      onPointerUp = (event) => {
        const press = this.press;
        if (!press || event.pointerId !== press.id) return;
        this.press = null;
        if (!press.dragging) return;
        this.wasDragged = event.type === 'pointerup';
        // Only the click that ends this drag; a later keyboard Enter must still open a link.
        setTimeout(() => (this.wasDragged = false), 400);
        this.classList.remove('is-dragging');
        if (!this.classList.contains('is-paused')) press.animation.play();
      };

      // A drag ends with a click on the link under the pointer in some browsers; don't follow it.
      onClick = (event) => {
        if (!this.wasDragged) return;
        this.wasDragged = false;
        event.preventDefault();
        event.stopPropagation();
      };

      // Stop the browser dragging the image or link itself instead of the row.
      onDragStart = (event) => event.preventDefault();

      onFocusIn = (event) => {
        // Keyboard focus only: a mouse press focuses the link too (Chrome), and would stop a drag.
        if (!event.target.closest('.brook-marquee__link') || !event.target.matches(':focus-visible')) return;
        this.classList.add('is-browsing');
        event.target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      };

      onFocusOut = (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.classList.remove('is-browsing');
        this.scrollLeft = 0;
      };

      toggle = () => {
        const paused = this.classList.toggle('is-paused');
        this.button.setAttribute('aria-pressed', paused);
        // After a drag the script controls the animation, and CSS play-state no longer applies.
        if (paused) this.animation?.pause();
        else this.animation?.play();
      };
    }
  );
}
